import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ListChecks, AlertTriangle, User as UserIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTasksByClient } from '@/hooks/useTasksByClient';
import { useTasks } from '@/hooks/useTasks';
import { useAppUsers } from '@/hooks/useAppUsers';
import { TaskDetailSheet } from '@/components/tasks/TaskDetailSheet';
import { EscalationTaskCompletionModal } from '@/components/tasks/EscalationTaskCompletionModal';
import { useEscalationContacts } from '@/hooks/useEscalationContacts';
import { cn } from '@/lib/utils';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'Em andamento',
  completed: 'Concluída',
  blocked: 'Bloqueada',
  cancelled: 'Cancelada',
};

const STATUS_VARIANT: Record<string, 'default' | 'outline' | 'secondary' | 'destructive'> = {
  pending: 'outline',
  in_progress: 'default',
  completed: 'secondary',
  blocked: 'destructive',
  cancelled: 'secondary',
};

interface Props {
  contactId: string | null;
  sessionId: string | null;
  onSelectSession?: (session: any) => void;
}

export function ContactTasksSection({ contactId, sessionId }: Props) {
  const queryClient = useQueryClient();
  const tasks = useTasksByClient({ contactId });
  const { updateTask, deleteTask } = useTasks();
  const { users } = useAppUsers();

  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [escalationTask, setEscalationTask] = useState<any | null>(null);
  const [showEscalationModal, setShowEscalationModal] = useState(false);

  const sessionIdsForEsc = useMemo(
    () => tasks.map((t: any) => t?.metadata?.session_id).filter(Boolean),
    [tasks]
  );
  const escalationContacts = useEscalationContacts(sessionIdsForEsc);

  const sorted = useMemo(() => {
    const order: Record<string, number> = { pending: 0, in_progress: 1, blocked: 2, completed: 3, cancelled: 4 };
    return [...tasks].sort((a: any, b: any) => {
      const sa = order[a.status] ?? 9;
      const sb = order[b.status] ?? 9;
      if (sa !== sb) return sa - sb;
      const da = new Date(a.due_date || a.created_at || 0).getTime();
      const db = new Date(b.due_date || b.created_at || 0).getTime();
      return db - da;
    });
  }, [tasks]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks-by-client'] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  const handleUpdate = async (id: string, data: any) => {
    await updateTask.mutateAsync({ id, ...data });
    invalidate();
  };

  const handleDelete = async (id: string) => {
    await deleteTask.mutateAsync(id);
    invalidate();
    setIsDetailOpen(false);
    setSelectedTask(null);
  };

  const handleCompleteFromSheet = async (task: any) => {
    if (task?.metadata?.source === 'cs_escalation') {
      setEscalationTask(task);
      setShowEscalationModal(true);
      setIsDetailOpen(false);
    } else {
      await updateTask.mutateAsync({
        id: task.id,
        status: 'completed',
        completed_at: new Date().toISOString(),
      });
      invalidate();
    }
  };

  const handleEscalationComplete = async (taskId: string, response: string) => {
    await updateTask.mutateAsync({
      id: taskId,
      notes: response,
      status: 'completed',
      completed_at: new Date().toISOString(),
    });
    invalidate();
    setShowEscalationModal(false);
    setEscalationTask(null);
  };

  const openTask = (t: any) => {
    setSelectedTask(t);
    setIsDetailOpen(true);
  };

  return (
    <>
      <Collapsible defaultOpen={false}>
        <div className="p-4">
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full flex items-center gap-2 mb-3 group">
              <ListChecks className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">Tarefas & Escalações</h3>
              <span className="text-xs text-muted-foreground">({sorted.length})</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto transition-transform [[data-state=open]_&]:rotate-180" />
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
            {sorted.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhuma tarefa para este cliente
              </p>
            ) : (
              <div className="space-y-2">
                {sorted.map((t: any) => {
                  const isEscalation = t?.metadata?.source === 'cs_escalation';
                  const isCurrentSession = isEscalation && t?.metadata?.session_id === sessionId;
                  const dateStr = t.due_date || t.created_at;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => openTask(t)}
                      className={cn(
                        'w-full text-left rounded-lg border bg-muted/50 hover:bg-muted transition-colors p-2.5 space-y-1.5',
                        isCurrentSession && 'border-primary/40 bg-primary/5'
                      )}
                    >
                      <div className="flex items-start gap-1.5">
                        <span className="text-sm font-medium line-clamp-2 flex-1 min-w-0">
                          {t.title || 'Tarefa'}
                        </span>
                        <Badge
                          variant={STATUS_VARIANT[t.status] || 'outline'}
                          className="text-[10px] h-4 px-1 flex-shrink-0"
                        >
                          {STATUS_LABEL[t.status] || t.status}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-1.5 flex-wrap">
                        {isEscalation && (
                          <Badge
                            variant="outline"
                            className="text-[10px] h-4 px-1 border-amber-500/40 text-amber-700 dark:text-amber-400 gap-1"
                          >
                            <AlertTriangle className="h-2.5 w-2.5" />
                            Escalação CS
                          </Badge>
                        )}
                        {isCurrentSession && (
                          <span className="text-[10px] text-primary font-medium">Chamado atual</span>
                        )}
                      </div>

                      <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1 truncate">
                          <UserIcon className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{t.assigned_user_name || 'Sem responsável'}</span>
                        </span>
                        {dateStr && (
                          <span className="flex-shrink-0">
                            {formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: ptBR })}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>

      <TaskDetailSheet
        task={selectedTask}
        open={isDetailOpen}
        onOpenChange={(open) => {
          setIsDetailOpen(open);
          if (!open) setSelectedTask(null);
        }}
        users={users as any}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onComplete={handleCompleteFromSheet}
        isUpdating={updateTask.isPending}
        isDeleting={deleteTask.isPending}
      />

      <EscalationTaskCompletionModal
        open={showEscalationModal}
        onClose={() => {
          setShowEscalationModal(false);
          setEscalationTask(null);
        }}
        task={escalationTask}
        contactName={
          escalationTask?.metadata?.session_id
            ? escalationContacts[escalationTask.metadata.session_id]?.contactName
            : null
        }
        onComplete={handleEscalationComplete}
        isSubmitting={updateTask.isPending}
      />
    </>
  );
}
